import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import VoiceToFormModal from "@/components/VoiceToFormModal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Microphone, CaretRight, User, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TYPES = ["Appartamento", "Villa", "Villetta", "Attico", "Bilocale", "Trilocale", "Monolocale", "Studio", "Loft", "Rustico", "Ufficio", "Negozio"];

export default function NewProperty() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    listing_type: "vendita",
    property_type: "Appartamento",
    price: "",
    hide_price: false,
    monthly_fees: "",
    deposit_months: "",
    contract_type: "",
    furnished: "",
    utilities_included: false,
    utilities_note: "",
    tenant_preference: "",
    surface: "",
    rooms: "",
    bathrooms: "",
    floor: "",
    address: "",
    civic_number: "",
    city: "",
    features: "",
    notes: "",
    sopralluogo_when: "",   // natural language: "domani alle 10"
    seller_name: "",
    seller_phone: "",
  });
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState(null);

  // Quando il modal vocale ritorna i campi, li applichiamo al form.
  // iter v28 r20 — il modal ora ritorna un terzo arg `{ overwrite }` che
  // viene da un toggle utente. Se overwrite=true: aggiorno anche i campi
  // già compilati (utente vuole CORREGGERE a voce). Se overwrite=false
  // (default): comportamento precedente, riempio solo i campi vuoti.
  const onVoiceApply = (fields, transcript, opts = {}) => {
    const overwrite = !!opts.overwrite;
    setForm((f) => {
      const merged = { ...f };
      const applied = [];
      Object.entries(fields || {}).forEach(([k, v]) => {
        if (!(k in merged)) return;                          // ignora campi sconosciuti
        if (!overwrite && merged[k] && String(merged[k]).trim()) return;
        merged[k] = String(v);
        applied.push(k);
      });
      setVoiceNotice({
        count: applied.length,
        fields: applied,
        transcript: (transcript || "").slice(0, 200),
      });
      return merged;
    });
  };

  const isRental = form.listing_type === "affitto";
  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // ── Seller contact picker (iter 287) ──────────────────────────────
  // Mini autocomplete che cerca tra i contatti esistenti dell'agente
  // mentre digita. Se sceglie un risultato → seller_contact_id popolato
  // (riusa anche telefono). Se digita un nome nuovo → al submit creiamo
  // automaticamente il contatto.
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showContactSugg, setShowContactSugg] = useState(false);
  const sellerSearchTimer = useRef(null);
  useEffect(() => {
    if (!showContactSugg) return;
    if (sellerSearchTimer.current) clearTimeout(sellerSearchTimer.current);
    const q = form.seller_name.trim();
    if (q.length < 2) { setContactSuggestions([]); return; }
    sellerSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { q, limit: 6 } });
        const list = Array.isArray(data) ? data : (data?.items || data?.contacts || []);
        setContactSuggestions(list);
      } catch { setContactSuggestions([]); }
    }, 220);
    return () => clearTimeout(sellerSearchTimer.current);
  }, [form.seller_name, showContactSugg]);

  const pickContact = (c) => {
    setForm((f) => ({
      ...f,
      seller_contact_id: c.id || "",
      seller_name: c.name || "",
      seller_phone: c.phone || f.seller_phone || "",
    }));
    setShowContactSugg(false);
  };

  const clearSeller = () => {
    setForm((f) => ({ ...f, seller_contact_id: "", seller_name: "", seller_phone: "" }));
    setShowContactSugg(false);
  };

  const onSubmit = async (e, opts = {}) => {
    e.preventDefault();
    const asDraft = !!opts.asDraft;
    setLoading(true);
    try {
      const payload = {
        title: form.title,
        listing_type: form.listing_type,
        property_type: form.property_type,
        price: form.price ? Number(form.price) : null,
        hide_price: !!form.hide_price,
        monthly_fees: isRental && form.monthly_fees ? Number(form.monthly_fees) : null,
        deposit_months: isRental && form.deposit_months ? parseInt(form.deposit_months, 10) : null,
        contract_type: isRental ? (form.contract_type || null) : null,
        furnished: isRental ? (form.furnished || null) : null,
        utilities_included: isRental ? Boolean(form.utilities_included) : null,
        utilities_note: isRental && form.utilities_note ? form.utilities_note : null,
        tenant_preference: isRental && form.tenant_preference ? form.tenant_preference : null,
        surface: form.surface ? Number(form.surface) : null,
        rooms: form.rooms ? parseInt(form.rooms, 10) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms, 10) : null,
        floor: form.floor || null,
        address: form.address || null,
        civic_number: form.civic_number || null,
        city: form.city || null,
        features: form.features ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : [],
        notes: form.notes || null,
      };
      const { data } = await api.post("/properties", payload);
      // iter 287 — collega l'annuncio a un cliente venditore:
      // 1. Se l'utente ha scelto un contatto esistente → riusa quell'id
      // 2. Se ha digitato un nuovo nome senza selezione → crea contatto al volo
      // 3. Poi linka contact_id ↔ property_id via PATCH /contacts/{id}
      let sellerContactId = form.seller_contact_id || "";
      if (!sellerContactId && form.seller_name.trim()) {
        try {
          const newC = await api.post("/contacts?force=1", {
            name: form.seller_name.trim(),
            phone: form.seller_phone.trim() || null,
            status: "tiepido",
            source: "annuncio",
            notes: `Venditore di "${form.title}"`,
          });
          sellerContactId = newC?.data?.id || "";
        } catch {/* non blocca creazione annuncio */}
      }
      if (sellerContactId) {
        try {
          await api.patch(`/contacts/${sellerContactId}`, {
            linked_property_ids: [data.id],
            relation: "venditore",
          });
        } catch { /* non blocca */ }
      }
      // iter 287 — rimosso auto-create del sopralluogo in agenda dalla creazione
      // annuncio: ora c'è il flusso dedicato /sopralluogo con voice recording,
      // mettere un timer placeholder qui creava un evento agenda senza dati reali.
      toast.success(
        asDraft ? "Bozza salvata" :
        sellerContactId ? "Immobile creato + venditore collegato" : "Immobile creato"
      );
      // iter v28 r21 — Dopo creazione atterra sul tab "Foto" (step 1 del
      // nuovo wizard). L'utente ha già parlato/scritto i dati, ora il
      // prossimo passo è caricare le foto, non rivedere il testo.
      nav(asDraft ? "/dashboard" : `/dashboard/property/${data.id}?tab=foto`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell title="Nuovo annuncio" breadcrumb="Nuovo annuncio">
        <button onClick={() => nav(-1)} className="text-sm text-[#6B6A65] hover:text-[#7C8B6E] flex items-center gap-2 mb-6" data-testid="back-btn">
          <ArrowLeft size={16} /> Indietro
        </button>
        <span className="overline">Nuovo annuncio</span>
        <h1 className="font-editorial text-4xl lg:text-5xl tracking-tight mt-3" data-testid="new-property-title">
          Aggiungi i <span className="italic-accent text-[#7C8B6E]">dettagli</span>.
        </h1>
        <p className="mt-3 text-[#6B6A65]">Più informazioni fornisci, più precisa sarà la descrizione.</p>

        {/* iter 250: dettatura inline — niente più redirect al Sopralluogo,
            l'agente resta sulla pagina e il modal voice compila i campi. */}
        <button
          type="button"
          onClick={() => setVoiceOpen(true)}
          className="mt-6 w-full flex items-center gap-3 p-4 bg-gradient-to-br from-[#7C8B6E]/10 to-white border border-[#7C8B6E]/30 rounded-lg hover:border-[#7C8B6E] hover:shadow-sm transition-all group text-left"
          data-testid="voice-form-banner"
        >
          <div className="w-10 h-10 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shrink-0">
            <Microphone size={16} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-[#1C1C1A]">
              {Object.values(form).some((v) => typeof v === "string" && v.trim())
                ? "Aggiungi o correggi a voce"
                : "Oppure: crealo parlando"}
            </div>
            <div className="text-[11.5px] text-[#6B6A65]">Descrivi l'immobile con la voce, Pronto compila i campi qui sotto. Le foto le aggiungi dopo.</div>
          </div>
          <CaretRight size={14} className="text-[#7C8B6E] shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </button>

        <form onSubmit={onSubmit} className="mt-10 card-flat p-8 space-y-6">
          {/* Banner di feedback dopo dettatura vocale */}
          {voiceNotice && (
            <div className="rounded-lg border border-[#7C8B6E]/40 bg-[#F1F4ED] px-4 py-3 flex items-start gap-3" data-testid="voice-applied-banner">
              <div className="w-7 h-7 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shrink-0 mt-0.5">
                <Microphone size={12} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-[#7C8B6E]">
                  Ho compilato {voiceNotice.count} {voiceNotice.count === 1 ? "campo" : "campi"} dalla tua voce
                </div>
                <div className="text-[11px] text-[#6B6A65] mt-0.5 leading-snug">
                  Controlla i campi qui sotto e correggi a mano se ho frainteso qualcosa.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setVoiceNotice(null)}
                className="text-[#9A968B] hover:text-[#1C1C1A] text-[11px] font-medium underline shrink-0"
                data-testid="voice-applied-dismiss"
              >
                Chiudi
              </button>
            </div>
          )}
          {/* Vendita / Affitto toggle */}
          <div>
            <label className="overline block mb-2">Tipo di annuncio</label>
            <div className="inline-flex rounded-lg bg-[#F1F4ED] p-1 border border-[#E8E5DE]" role="tablist" data-testid="listing-type-toggle">
              {[
                { val: "vendita", label: "Vendita" },
                { val: "affitto", label: "Affitto" },
              ].map((opt) => (
                <button
                  key={opt.val}
                  type="button"
                  role="tab"
                  aria-selected={form.listing_type === opt.val}
                  onClick={() => setForm((f) => ({ ...f, listing_type: opt.val }))}
                  className={`px-5 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    form.listing_type === opt.val
                      ? "bg-white text-[#1C1C1A] shadow-sm"
                      : "text-[#6B6A65] hover:text-[#1C1C1A]"
                  }`}
                  data-testid={`listing-type-${opt.val}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Titolo*</label>
            <input required data-vista value={form.title} onChange={update("title")} placeholder={isRental ? "Es. Bilocale arredato — Navigli, Milano" : "Es. Attico con vista — Brera, Milano"} data-testid="title-input" />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="overline block mb-2">Tipologia</label>
              <select data-vista value={form.property_type} onChange={update("property_type")} data-testid="type-select" className="w-full">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">{isRental ? "Canone mensile (€)" : "Prezzo (€)"}</label>
              <input
                type="number"
                data-vista
                value={form.price}
                onChange={update("price")}
                placeholder={isRental ? "950" : "285000"}
                data-testid="price-input"
                disabled={form.hide_price}
              />
              {/* iter v28r9: toggle Trattativa riservata.
                  Quando attivo, il prezzo non viene mostrato sulla pagina
                  pubblica (al suo posto compare "Trattativa riservata") né
                  sui materiali (post, story, reel, brochure). */}
              <label className="mt-2 flex items-center gap-2 text-[13px] text-[#6B6A65] cursor-pointer select-none" data-testid="hide-price-toggle">
                <input
                  type="checkbox"
                  checked={!!form.hide_price}
                  onChange={(e) => setForm((f) => ({ ...f, hide_price: e.target.checked }))}
                  className="accent-[#7C8B6E]"
                />
                Trattativa riservata (nascondi il prezzo)
              </label>
            </div>
          </div>

          {/* Rental-specific fields — visible only when affitto */}
          {isRental && (
            <div className="bg-[#FAFAF7] border border-[#E8E5DE] rounded-md p-5 space-y-5" data-testid="rental-fields">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold">Dettagli affitto</div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Spese condominiali mensili (€)</label>
                  <input type="number" data-vista value={form.monthly_fees} onChange={update("monthly_fees")} placeholder="85" data-testid="monthly-fees-input" />
                </div>
                <div>
                  <label className="overline block mb-2">Cauzione (mensilità)</label>
                  <input type="number" data-vista value={form.deposit_months} onChange={update("deposit_months")} placeholder="2" data-testid="deposit-months-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Tipo contratto</label>
                  <select data-vista value={form.contract_type} onChange={update("contract_type")} data-testid="contract-type-select" className="w-full">
                    <option value="">Scegli…</option>
                    <option value="4+4 libero">4 + 4 libero</option>
                    <option value="3+2 concordato">3 + 2 concordato</option>
                    <option value="Transitorio">Transitorio (1-18 mesi)</option>
                    <option value="Studenti">Studenti universitari</option>
                    <option value="Commerciale 6+6">Commerciale 6+6</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Arredamento</label>
                  <select data-vista value={form.furnished} onChange={update("furnished")} data-testid="furnished-select" className="w-full">
                    <option value="">Scegli…</option>
                    <option value="arredato">Arredato</option>
                    <option value="semi-arredato">Semi-arredato</option>
                    <option value="non-arredato">Non arredato</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.utilities_included} onChange={update("utilities_included")} className="mt-1" data-testid="utilities-included-checkbox" />
                  <div>
                    <div className="text-[13px] font-medium text-[#1C1C1A]">Utenze incluse nel canone</div>
                    <div className="text-[11.5px] text-[#6B6A65]">Spunta se acqua, luce, gas o condominio sono già compresi</div>
                  </div>
                </label>
                {form.utilities_included && (
                  <input data-vista value={form.utilities_note} onChange={update("utilities_note")} placeholder="Es. solo acqua e condominio · opzionale" data-testid="utilities-note-input" className="mt-2" />
                )}
              </div>
              <div>
                <label className="overline block mb-2">Preferenza conduttore <span className="text-[11px] text-[#9A968B] normal-case tracking-normal">(opzionale)</span></label>
                <input data-vista value={form.tenant_preference} onChange={update("tenant_preference")} placeholder="Es. Lavoratore · no animali" data-testid="tenant-preference-input" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-5">
            <div>
              <label className="overline block mb-2">Superficie (m²)</label>
              <input type="number" data-vista value={form.surface} onChange={update("surface")} placeholder="120" data-testid="surface-input" />
            </div>
            <div>
              <label className="overline block mb-2">Locali</label>
              <input type="number" data-vista value={form.rooms} onChange={update("rooms")} placeholder="4" data-testid="rooms-input" />
            </div>
            <div>
              <label className="overline block mb-2">Bagni</label>
              <input type="number" data-vista value={form.bathrooms} onChange={update("bathrooms")} placeholder="2" data-testid="bathrooms-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="overline block mb-2">Piano</label>
              <input data-vista value={form.floor} onChange={update("floor")} placeholder="3°" data-testid="floor-input" />
            </div>
            <div>
              <label className="overline block mb-2">Città</label>
              <input data-vista value={form.city} onChange={update("city")} placeholder="Milano" data-testid="city-input" />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Indirizzo</label>
            {/* iter v28 r425 — User M1335: via e civico in 2 campi separati così
                l'estensione Chrome li compila distintamente su Immobiliare/Idealista
                senza doverli estrarre con una regex fragile. */}
            <div className="grid grid-cols-[1fr_120px] gap-2">
              <input
                data-vista
                value={form.address}
                onChange={update("address")}
                placeholder="Via / Piazza"
                data-testid="address-input"
              />
              <input
                data-vista
                value={form.civic_number}
                onChange={update("civic_number")}
                placeholder="Civico (es. 42, 42/A)"
                data-testid="civic-input"
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Caratteristiche (separate da virgola)</label>
            <input data-vista value={form.features} onChange={update("features")} placeholder="Balcone, Garage, Ascensore, Pronto, Ristrutturato" data-testid="features-input" />
          </div>

          <div>
            <label className="overline block mb-2">Note per Pronto</label>
            <textarea data-vista rows={4} value={form.notes} onChange={update("notes")} placeholder="Spazio cucina abitabile, zona tranquilla vicino metro, vista Duomo..." data-testid="notes-input" />
          </div>

          {/* iter 287 — Cliente venditore (rimossa sezione "Sopralluogo in
              agenda" che confondeva: se sto creando un annuncio, l'agenda
              del sopralluogo è un altro flusso). Qui invece registriamo
              CHI è il venditore dell'immobile — fondamentale per il CRM. */}
          <div className="bg-[#FAFAF7] border border-[#E8E5DE] rounded-md p-4" data-testid="seller-contact-section">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold mb-1">Cliente venditore · Opzionale</div>
            <div className="text-[13px] text-[#1C1C1A] font-medium mb-3 flex items-center gap-2">
              <User size={16} weight="duotone" />
              Di chi è questo annuncio?
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="relative">
                <label className="block text-[11px] uppercase tracking-[0.1em] font-semibold text-[#9A968B] mb-1">
                  Nome venditore
                </label>
                <div className="relative">
                  <input
                    data-vista
                    value={form.seller_name}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, seller_name: e.target.value, seller_contact_id: "" }));
                      setShowContactSugg(true);
                    }}
                    onFocus={() => setShowContactSugg(true)}
                    onBlur={() => setTimeout(() => setShowContactSugg(false), 150)}
                    placeholder="es. Luca Ferrari"
                    data-testid="seller-name-input"
                  />
                  {form.seller_contact_id && (
                    <button
                      type="button"
                      onClick={clearSeller}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9A968B] hover:text-[#1C1C1A]"
                      title="Rimuovi collegamento"
                      data-testid="seller-clear-btn"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  )}
                </div>
                {showContactSugg && contactSuggestions.length > 0 && (
                  <div
                    className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E5E7E0] rounded-md shadow-lg max-h-[220px] overflow-y-auto"
                    data-testid="seller-suggestions"
                  >
                    {contactSuggestions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickContact(c); }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F5F2EB] border-b border-[#F0EDE5] last:border-b-0"
                      >
                        <div className="text-[13px] font-medium text-[#1C1C1A]">{c.name}</div>
                        <div className="text-[11px] text-[#6B6A65]">
                          {[c.phone, c.email].filter(Boolean).join(" · ") || "Nessun contatto"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {form.seller_contact_id && (
                  <div className="text-[10.5px] text-[#7C8B6E] mt-1 flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#7C8B6E]" />
                    Collegato al contatto esistente
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.1em] font-semibold text-[#9A968B] mb-1">
                  Telefono · Opzionale
                </label>
                <input
                  data-vista
                  value={form.seller_phone}
                  onChange={update("seller_phone")}
                  placeholder="+39 333 1234567"
                  data-testid="seller-phone-input"
                />
              </div>
            </div>
            <div className="text-[10.5px] text-[#9A968B] mt-2 leading-[1.5]">
              Se è un contatto già nel tuo CRM, scegli dalla lista mentre digiti. Altrimenti Pronto lo crea per te automaticamente quando salvi l'annuncio.
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-[#E5E7E0] flex-wrap">
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="create-property-btn">
              {loading ? "Creazione..." : <>Crea immobile <ArrowRight size={16} weight="bold" /></>}
            </button>
            <button
              type="button"
              disabled={loading || !form.title.trim()}
              onClick={(e) => onSubmit(e, { asDraft: true })}
              className="btn-outline disabled:opacity-60"
              data-testid="save-draft-btn"
              title={!form.title.trim() ? "Inserisci almeno il titolo" : "Salva come bozza, completi dopo"}
            >
              Salva bozza
            </button>
            <button type="button" onClick={() => nav(-1)} className="text-sm text-[#6B6A65] hover:text-[#1C1C1A] underline ml-auto" data-testid="cancel-btn">Annulla</button>
          </div>
        </form>

        <VoiceToFormModal
          open={voiceOpen}
          onClose={() => setVoiceOpen(false)}
          onApply={onVoiceApply}
          allowOverwrite={Object.values(form).some((v) => typeof v === "string" && v.trim())}
          mode={Object.values(form).some((v) => typeof v === "string" && v.trim()) ? "edit" : "create"}
          existingFields={{
            title: form.title || null,
            city: form.city || null,
            price: form.price || null,
            surface: form.surface || null,
            rooms: form.rooms || null,
            property_type: form.property_type || null,
          }}
        />
      </AppShell>
  );
}
